/**
 * PlatformPage — platform-owner console: every agency, subscription state, suspend/restore,
 * plan changes, impersonation, deletion and bulk test-data clean-up.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import AgencyRow from "@/components/platform/AgencyRow";
import { DeleteAgencyDialog, BulkDeleteDialog } from "@/components/platform/PlatformDialogs";
import AgencyUsersDialog from "@/components/platform/AgencyUsersDialog";
import AuditDrawer from "@/components/platform/AuditDrawer";
import StripeWebhookCard from "@/components/platform/StripeWebhookCard";
import BillingNoticesDrawer from "@/components/platform/BillingNoticesDrawer";
import { Building2, Loader2, RefreshCw, ScrollText, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = [["all", "All"], ["active", "Active"], ["paying", "Paying"], ["paused", "Suspended"], ["pending", "Never subscribed"], ["cancelled", "Cancelled"], ["test", "Test data"]];

export default function PlatformPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [noticesFor, setNoticesFor] = useState(null);
  const [usersFor, setUsersFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setData((await axios.get(`${API}/platform/agencies`)).data); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not load platform data"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    return data.agencies.filter((a) => {
      if (q && !`${a.name} ${a.code || ""} ${a.email || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
      if (filter === "all") return true;
      if (filter === "paying") return a.status === "active" && a.mrr > 0;
      if (filter === "pending") return a.status === "pending" || a.status === "none";
      if (filter === "cancelled") return ["cancelled", "expired", "unpaid", "past_due"].includes(a.status);
      if (filter === "test") return a.is_test;
      return a.status === filter;
    });
  }, [data, q, filter]);

  const act = async (agency, action, body) => {
    try {
      const res = await axios.post(`${API}/platform/agencies/${agency.id}/${action}`, body || {});
      if (action === "impersonate") {
        localStorage.setItem("platform_owner_token", localStorage.getItem("token") || "");
        localStorage.setItem("platform_impersonating", res.data.agency_name || agency.name);
        localStorage.setItem("token", res.data.access_token);
        window.location.href = "/dashboard";
        return;
      }
      toast.success({ suspend: "Access suspended", restore: "Access restored", plan: "Plan updated", "cancel-stripe": "Stripe subscription cancelled" }[action] || "Done");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
  };

  const toggleAll = () => setSelected(selected.size === rows.length ? new Set() : new Set(rows.filter((r) => r.id !== user?.agency_id).map((r) => r.id)));

  if (user && !user.is_platform_owner) {
    return <Layout><div className="p-10 text-center text-slate-500" data-testid="platform-forbidden">Platform owner access required.</div></Layout>;
  }
  const o = data?.overview;

  return (
    <Layout>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Building2 className="w-6 h-6 text-teal-600" /> Platform</h1>
          <p className="text-slate-500">Every agency on CarePilot — subscriptions, access and clean-up</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setAuditOpen(true)} className="gap-1" data-testid="platform-audit-btn"><ScrollText className="w-4 h-4" /> Audit log</Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1" data-testid="platform-refresh-btn"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
        </div>
      </div>

      <StripeWebhookCard />

      {o && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="platform-overview">
          {[["Agencies", o.agencies], ["Active subscribers", o.active, "text-emerald-700"], ["Paying", o.paying, "text-emerald-700"], ["Free plan", o.free], ["Suspended", o.paused, "text-rose-700"], ["Never subscribed", o.pending, "text-amber-700"], ["Est. MRR", `$${o.mrr.toLocaleString()}`, "text-teal-700"]].map(([label, value, cls]) => (
            <div key={label} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`platform-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
              <p className={`text-2xl font-bold mt-1 ${cls || "text-slate-900"}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Input placeholder="Search agencies…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 max-w-xs" data-testid="platform-search" />
        {FILTERS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`platform-filter-${k}`}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {label}{k === "test" && o ? ` (${o.test})` : ""}
          </button>
        ))}
        {selected.size > 0 && (
          <Button size="sm" variant="destructive" onClick={() => setBulkOpen(true)} className="ml-auto gap-1" data-testid="platform-bulk-delete-btn">
            <Trash2 className="w-4 h-4" /> Delete selected ({selected.size})
          </Button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        {loading && !data ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 w-8"><input type="checkbox" checked={rows.some((r) => r.id !== user?.agency_id) && selected.size === rows.filter((r) => r.id !== user?.agency_id).length} onChange={toggleAll} data-testid="platform-select-all" /></th>
                <th className="px-3 py-2">Agency</th>
                <th className="px-3 py-2">Plan</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Usage</th>
                <th className="px-3 py-2">Last login</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400 italic">No agencies match</td></tr>}
              {rows.map((a) => (
                <AgencyRow key={a.id} agency={a} plans={data.plans} isSelf={a.id === user?.agency_id} selected={selected.has(a.id)}
                  onSelect={() => setSelected((s) => { const n = new Set(s); n.has(a.id) ? n.delete(a.id) : n.add(a.id); return n; })}
                  onAction={(action, body) => act(a, action, body)} onDelete={() => setDeleting(a)} onNotices={() => setNoticesFor(a)} onUsers={() => setUsersFor(a)} />
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-xs text-slate-400 mt-2">{rows.length} of {data?.agencies.length || 0} agencies · your own agency can't be suspended or deleted from here.</p>

      {deleting && <DeleteAgencyDialog agency={deleting} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); load(); }} />}
      {bulkOpen && <BulkDeleteDialog agencies={rows.filter((r) => selected.has(r.id))} onClose={() => setBulkOpen(false)} onDeleted={() => { setBulkOpen(false); setSelected(new Set()); load(); }} />}
      <AuditDrawer open={auditOpen} onClose={() => setAuditOpen(false)} />
      <BillingNoticesDrawer agency={noticesFor} onClose={() => setNoticesFor(null)} />
      {usersFor && <AgencyUsersDialog agency={usersFor} onClose={() => setUsersFor(null)} onChanged={load} />}
    </Layout>
  );
}
